// src/utils/shelfHandler.ts
// 本棚関連 API クライアント（rl-api-7-shelves の一覧取得 / rl-api-6-books の所有状況・単冊登録）
//
// 契約サマリ:
//   GET  /shelves                                     → 本棚一覧
//   GET  /books?rl_book_id=X&shelf_status=true         → 指定書籍の所有状況（冊数・本棚名一覧）
//   POST /books  operation_type=register_book          → 1冊を即時登録（本棚指定は任意）

import axios from 'axios';
import { NEW_API } from '../config/api';

function authHeaders(idToken: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
}

export interface ShelfSummary {
  shelf_id: string;
  shelf_name: string;
}

/** GET /shelves — 本棚一覧（プルダウン表示用） */
export async function fetchShelves(idToken: string): Promise<ShelfSummary[]> {
  const res = await axios.get(NEW_API.SHELVES, { headers: authHeaders(idToken) });
  const items = res.data?.data?.items ?? [];
  return items.map((s: any) => ({
    shelf_id: String(s.rl_global_shelf_id),
    shelf_name: String(s.global_shelf_name ?? ''),
  }));
}

export interface ShelfDetail {
  rl_global_shelf_id: string;
  global_shelf_name: string;
  global_shelf_color_code: string;
  global_shelf_order: number;
  global_shelf_open_level: string;
  global_shelf_description: string | null;
  book_count?: number;
}

/** GET /shelves — 本棚管理画面用の生データ一覧 */
export async function fetchShelvesRaw(idToken: string): Promise<ShelfDetail[]> {
  const res = await axios.get(NEW_API.SHELVES, { headers: authHeaders(idToken) });
  return res.data?.data?.items ?? [];
}

/** POST /shelves operation_type=create_shelf */
export async function createShelf(
  params: { shelfName: string; colorCode?: string; openLevel?: string; description?: string },
  idToken: string,
): Promise<ShelfDetail> {
  const body: Record<string, unknown> = {
    operation_type: 'create_shelf',
    shelf_name: params.shelfName,
    color_code: params.colorCode ?? '#808080',
    open_level: params.openLevel ?? 'private',
  };
  if (params.description != null) body.description = params.description;
  const res = await axios.post(NEW_API.SHELVES, body, { headers: authHeaders(idToken) });
  return res.data?.data;
}

/** POST /shelves operation_type=update_shelf */
export async function updateShelf(
  params: { shelfId: string; shelfName?: string; colorCode?: string; openLevel?: string; description?: string },
  idToken: string,
): Promise<ShelfDetail> {
  const body: Record<string, unknown> = {
    operation_type: 'update_shelf',
    shelf_id: params.shelfId,
  };
  if (params.shelfName != null) body.shelf_name = params.shelfName;
  if (params.colorCode != null) body.color_code = params.colorCode;
  if (params.openLevel != null) body.open_level = params.openLevel;
  if (params.description != null) body.description = params.description;
  const res = await axios.post(NEW_API.SHELVES, body, { headers: authHeaders(idToken) });
  return res.data?.data;
}

/** POST /shelves operation_type=delete_shelf */
export async function deleteShelf(shelfId: string, idToken: string): Promise<void> {
  await axios.post(
    NEW_API.SHELVES,
    { operation_type: 'delete_shelf', shelf_id: shelfId },
    { headers: authHeaders(idToken) },
  );
}

/** POST /shelves operation_type=reorder_shelves */
export async function reorderShelves(orderedIds: string[], idToken: string): Promise<void> {
  await axios.post(
    NEW_API.SHELVES,
    { operation_type: 'reorder_shelves', ordered_ids: orderedIds },
    { headers: authHeaders(idToken) },
  );
}

/** POST /books operation_type=set_shelf_books_open_level — 本棚内の全ての本の公開設定を一括変更 */
export async function setShelfBooksOpenLevel(
  shelfId: string,
  openLevel: string,
  idToken: string,
): Promise<void> {
  await axios.post(
    NEW_API.BOOKS,
    { operation_type: 'set_shelf_books_open_level', shelf_id: shelfId, open_level: openLevel },
    { headers: authHeaders(idToken) },
  );
}

export interface BookShelfEntry {
  global_book_id: string;
  shelf_id: string | null;
  shelf_name: string;
}

export interface BookShelfStatus {
  rl_book_id: number;
  owned_count: number;
  shelves: BookShelfEntry[];
}

/** GET /books?rl_book_id=X&shelf_status=true — この本の所有状況（冊数・本棚名一覧） */
export async function fetchBookShelfStatus(
  rlBookId: number,
  idToken: string,
): Promise<BookShelfStatus> {
  const res = await axios.get(NEW_API.BOOKS, {
    headers: authHeaders(idToken),
    params: { rl_book_id: rlBookId, shelf_status: 'true' },
  });
  return res.data?.data as BookShelfStatus;
}

/**
 * POST /books operation_type=bulk_register — 1冊をキュー登録して本棚に追加（shelfId=null で本棚未分類）
 * SQS経由の非同期処理のため、呼び出し直後はまだ反映されていないことがある
 * （本棚状態の再取得は少し待ってから行うこと）
 */
export async function registerBookToShelf(
  rlBookId: number,
  shelfId: string | null,
  idToken: string,
): Promise<void> {
  const item: Record<string, unknown> = { rl_book_id: rlBookId };
  if (shelfId) item.shelf_id = shelfId;
  await axios.post(
    NEW_API.BOOKS,
    { operation_type: 'bulk_register', items: [item] },
    { headers: authHeaders(idToken) },
  );
}
